import type { ApiResponse } from "@/types";
import { SESSION_TERMINATED_EVENT } from "@/lib/ws-events";
import { clearAuthStorage } from "@/lib/auth-storage";
import { refreshAccessToken, setCoordinatorOnTokenRefreshed } from "@/lib/auth-refresh-coordinator";
import { CLIENT_APP_HEADER } from "@/lib/auth-refresh-core";

/** Backend uses RFC 9457 `error.detail`; UI reads `error.message` — align so toasts show real reasons. */
function normalizeApiEnvelope<T>(json: unknown): ApiResponse<T> {
  if (!json || typeof json !== "object") {
    return { success: false, error: { code: "INVALID_RESPONSE", message: "Invalid response" } };
  }
  const o = json as Record<string, unknown>;
  if (o.success === false && o.error && typeof o.error === "object") {
    const err = o.error as Record<string, unknown>;
    const detail = err.detail;
    const msg = err.message;
    const title = err.title;
    if (typeof detail === "string" && detail.trim() && (typeof msg !== "string" || !msg.trim())) {
      err.message = detail;
    } else if (
      (typeof msg !== "string" || !msg.trim()) &&
      typeof title === "string" &&
      title.trim()
    ) {
      err.message = title;
    }
  }
  return json as ApiResponse<T>;
}

function networkFailureMessage(err: unknown): string {
  if (!(err instanceof Error)) return "Request failed";
  const m = err.message || "";
  if (err instanceof TypeError && (m === "Failed to fetch" || m.includes("fetch") || m.includes("Load failed"))) {
    return "Could not reach the API. Open the app using the same host and port Vite prints when you start the frontend (so requests stay on /api/v1 and use the dev proxy). Avoid mixing that with another hostname/IP or a bare VITE_API_URL that the browser cannot reach. For a separate API host, set VITE_API_URL to that API origin and ensure CORS allows this site.";
  }
  return m;
}

const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "/api/v1";

let accessToken: string | null = null;
let activeStudioId: string = "all";
let refreshTokenValue: string | null = null;
export type ApiClientRequestOptions = Omit<RequestInit, "body"> & {
  idempotent?: boolean;
  skipAutoRedirect?: boolean;
  studioId?: string;
  body?: BodyInit | null;
};

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setRefreshToken(token: string | null) {
  refreshTokenValue = token;
}

export function getRefreshToken(): string | null {
  return refreshTokenValue;
}

export function setOnTokenRefreshed(cb: ((accessToken: string, refreshToken: string | null) => void) | null) {
  setCoordinatorOnTokenRefreshed(
    cb
      ? (accessToken, refreshToken) => cb(accessToken, refreshToken)
      : null,
  );
}

export function setActiveStudioId(id: string) {
  activeStudioId = id;
}

export function getActiveStudioId(): string {
  return activeStudioId;
}

export function terminateSessionAndRedirect(): void {
  setAccessToken(null);
  setRefreshToken(null);
  clearAuthStorage();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_TERMINATED_EVENT));
    window.location.href = "/login?session_expired=true";
  }
}

async function readResponseJson(res: Response): Promise<unknown | null> {
  try {
    const text = await res.text();
    if (!text.trim()) return {};
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * POST idempotency keys must be UUIDs (server validates). `crypto.randomUUID()` throws outside a
 * secure context (e.g. plain http:// on a LAN IP), which broke archive and other POSTs with no clear API error.
 */
export function newIdempotencyKey(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    /* randomUUID throws in non-secure contexts */
  }
  const bytes = new Uint8Array(16);
  try {
    globalThis.crypto?.getRandomValues?.(bytes);
  } catch {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function refreshToken(skipAutoRedirect: boolean): Promise<boolean> {
  return refreshAccessToken({ skipAutoRedirect });
}

type ExecuteRequestParams = {
  endpoint: string;
  method: string;
  body?: BodyInit | null;
  extraHeaders?: Record<string, string>;
  skipAutoRedirect?: boolean;
  studioId?: string;
  idempotent?: boolean;
  injectEmptyJsonBody?: boolean;
  signal?: AbortSignal;
};

async function executeApiRequest<T>(params: ExecuteRequestParams): Promise<ApiResponse<T>> {
  const {
    endpoint,
    method,
    body: callerBody,
    extraHeaders,
    skipAutoRedirect,
    studioId: studioIdOverride,
    idempotent,
    injectEmptyJsonBody,
    signal,
  } = params;

  const isFormData = callerBody instanceof FormData;
  const needsJsonBody = injectEmptyJsonBody && !callerBody && ["POST", "PUT", "PATCH"].includes(method);
  const body = callerBody ?? (needsJsonBody ? JSON.stringify({}) : undefined);

  const headers: Record<string, string> = {
    ...(body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
    // Tells the API which browser SPA is calling so it can scope the refresh
    // cookie namespace (customer app vs `/management`, same origin).
    [CLIENT_APP_HEADER]: "web",
    ...extraHeaders,
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const studioHeader = studioIdOverride ?? activeStudioId;
  if (studioHeader) {
    headers["X-Studio-Id"] = studioHeader;
  }

  if (idempotent && method === "POST") {
    headers["Idempotency-Key"] = newIdempotencyKey();
  }

  const fetchInit: RequestInit = {
    method,
    headers,
    ...(body !== undefined ? { body } : {}),
    credentials: "include",
    ...(signal ? { signal } : {}),
  };

  try {
    const res = await fetch(`${BASE_URL}${endpoint}`, fetchInit);

    const isUnauthenticatedAuthAttempt =
      res.status === 401 &&
      (endpoint === "/auth/login" || endpoint.startsWith("/auth/login/"));

    if (isUnauthenticatedAuthAttempt) {
      const json = await readResponseJson(res);
      if (json === null) {
        return {
          success: false,
          error: {
            code: "INVALID_RESPONSE",
            message: `Could not read server response (${res.status})`,
          },
        };
      }
      return normalizeApiEnvelope(json);
    }

    if (res.status === 401 && !endpoint.includes("/auth/refresh")) {
      const refreshed = await refreshToken(!!skipAutoRedirect);
      if (refreshed) {
        headers["Authorization"] = `Bearer ${accessToken}`;
        const retry = await fetch(`${BASE_URL}${endpoint}`, fetchInit);
        const retryJson = await readResponseJson(retry);
        if (retryJson === null) {
          return {
            success: false,
            error: {
              code: "INVALID_RESPONSE",
              message: `Could not read server response (${retry.status})`,
            },
          };
        }
        const retryOut = normalizeApiEnvelope<T>(retryJson);
        if (!retry.ok && retryOut.success !== false) {
          return {
            success: false,
            error: {
              code: `HTTP_${retry.status}`,
              message: retry.statusText || `Request failed (${retry.status})`,
            },
          };
        }
        return retryOut;
      }
      return { success: false, error: { code: "UNAUTHORIZED", message: "Session expired" } };
    }

    if (res.status === 429) {
      const retryAfterHeader = res.headers.get("Retry-After");
      const retryAfterFromHeader =
        retryAfterHeader && Number.isFinite(Number(retryAfterHeader)) && Number(retryAfterHeader) > 0
          ? Math.floor(Number(retryAfterHeader))
          : undefined;
      const rateLimitJson = await readResponseJson(res);
      if (rateLimitJson !== null) {
        const normalized = normalizeApiEnvelope<T>(rateLimitJson);
        if (normalized.success === false) {
          const retryAfterFromBody =
            typeof normalized.error?.retryAfter === "number" && Number.isFinite(normalized.error.retryAfter) && normalized.error.retryAfter > 0
              ? Math.floor(normalized.error.retryAfter)
              : undefined;
          const retryAfter = retryAfterFromBody ?? retryAfterFromHeader;
          return {
            success: false,
            error: {
              ...normalized.error,
              code: normalized.error?.code || "RATE_LIMIT_EXCEEDED",
              message:
                normalized.error?.message ||
                `Too many requests. Please try again in ${retryAfter ?? "a few"} seconds.`,
              ...(retryAfter ? { retryAfter } : {}),
            },
          };
        }
      }
      return {
        success: false,
        error: {
          code: "RATE_LIMIT_EXCEEDED",
          message: `Too many requests. Please try again in ${retryAfterFromHeader ?? "a few"} seconds.`,
          ...(retryAfterFromHeader ? { retryAfter: retryAfterFromHeader } : {}),
        },
      };
    }

    const json = await readResponseJson(res);
    if (json === null) {
      return {
        success: false,
        error: {
          code: "INVALID_RESPONSE",
          message: `Could not read server response (${res.status} ${res.statusText || ""})`.trim(),
        },
      };
    }
    const out = normalizeApiEnvelope<T>(json);
    if (!res.ok && out.success !== false) {
      return {
        success: false,
        error: {
          code: `HTTP_${res.status}`,
          message:
            (out.error && typeof out.error.message === "string" && out.error.message.trim()) ||
            res.statusText ||
            `Request failed (${res.status})`,
        },
      };
    }
    return out;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      return { success: false, error: { code: "ABORTED", message: "Request cancelled" } };
    }
    return { success: false, error: { code: "NETWORK_ERROR", message: networkFailureMessage(e) } };
  }
}

export async function apiClient<T>(
  endpoint: string,
  options: ApiClientRequestOptions = {},
): Promise<ApiResponse<T>> {
  const { studioId, idempotent, skipAutoRedirect, body, method, headers, ...rest } = options;
  return executeApiRequest<T>({
    endpoint,
    method: (method ?? "GET").toUpperCase(),
    body: body ?? null,
    extraHeaders: headers as Record<string, string> | undefined,
    skipAutoRedirect,
    studioId,
    idempotent,
    injectEmptyJsonBody: true,
    signal: rest.signal ?? undefined,
  });
}

/** Multipart POST/PUT — omits JSON Content-Type so the browser sets the boundary. */
export async function apiClientMultipart<T>(
  endpoint: string,
  formData: FormData,
  options: Omit<ApiClientRequestOptions, "body"> = {},
): Promise<ApiResponse<T>> {
  const { studioId, idempotent = true, skipAutoRedirect, method = "POST", headers, ...rest } = options;
  return executeApiRequest<T>({
    endpoint,
    method: method.toUpperCase(),
    body: formData,
    extraHeaders: headers as Record<string, string> | undefined,
    skipAutoRedirect,
    studioId,
    idempotent,
    injectEmptyJsonBody: false,
    signal: rest.signal ?? undefined,
  });
}
